import React, { useState, useEffect } from "react";
import api from "../../../../api";
import { Card, Row, Col, Table, Button, Modal, Badge } from "react-bootstrap";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { useParams } from "react-router-dom";
import VerifiedCopyActions from "../../Common/VerifiedCopyActions";

const COLORS = ["#FFBB28", "#00C49F", "#0088FE", "#FF4136", "#AA66CC"];

const POTab = () => {
const { id: workOrderId } = useParams();

 const [poData, setPoData] = useState([]);
 const [recentPOs, setRecentPOs] = useState([]);
 const [allPOs, setAllPOs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");

 // PDF Modal
 const [showPdf, setShowPdf] = useState(false);
 const [pdfURL, setPdfURL] = useState("");
 const [loadingPdf, setLoadingPdf] = useState(false);

 const fetchPOs = async () => {
 try {
 const res = await api.get(`/purchase-orders/work-order/${workOrderId}`);

 if (!res.data.success) {
 setError("Failed to load purchase orders");
 return;
 }

 const pos = res.data.data || [];
 setAllPOs(pos);

 // ===============================
 // STATUS COUNT (Pie Chart)
 // ===============================
 const statusCounts = pos.reduce((acc, po) => {
 const s = po.status || "Unknown";
 acc[s] = (acc[s] || 0) + 1;
 return acc;
 }, {});

 const pieData = Object.entries(statusCounts).map(([name, value]) => ({
 name,
 value,
 }));

 setPoData(pieData);

 // ===============================
 // RECENT 5 POs
 // ===============================
 const sorted = [...pos].sort(
 (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
 );

 setRecentPOs(sorted.slice(0, 5));

 } catch (err) {
 setError("Error fetching purchase orders");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 if (workOrderId) fetchPOs();
 }, [workOrderId]);

 const handlePreviewPDF = async (id) => {
 try {
 setLoadingPdf(true);

 const res = await api.get(`/purchase-orders/${id}/pdf`, {
 responseType: "blob",
 });

 const blob = new Blob([res.data], { type: "application/pdf" });
 const url = window.URL.createObjectURL(blob);

 setPdfURL(url);
 setShowPdf(true);
 } catch (err) {
 alert("Failed to preview PDF");
 } finally {
 setLoadingPdf(false);
 }
 };

 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/purchase-orders/${id}/pdf`, {
 responseType: "blob",
 });

 const url = window.URL.createObjectURL(new Blob([res.data]));
 const a = document.createElement("a");
 a.href = url;
 a.download = `PO-${id}.pdf`;
 a.click();
 } catch (err) {
 alert("Failed to download PDF");
 }
 };

 const formatDate = (date) => {
 if (!date) return "---";
 return new Date(date).toLocaleDateString("en-US", {
 year: "numeric",
 month: "short",
 day: "numeric",
 });
 };

 const statusColor = (status) => {
 switch (status) {
 case "Pending":
 return "warning";
 case "Issued":
 return "info";
 case "Accepted":
 return "success";
 case "Rejected":
 return "danger";
 case "Delivered":
 return "primary";
 default:
 return "secondary";
 }
 };

 if (loading) return <p>Loading purchase orders...</p>;
 if (error) return <p className="text-danger">{error}</p>;

 return (
 <>
 <Row className="g-4">
 {/* PIE CHART */}
 <Col md={6}>
 <Card className="shadow-sm border-0">
 <Card.Body>
 <h5 className="mb-3 fw-bold">PO Status Distribution</h5>

 {poData.length === 0 ? (
 <p className="text-muted">No Purchase Orders for this project.</p>
 ) : (
 <PieChart width={350} height={300}>
 <Pie
 data={poData}
 dataKey="value"
 nameKey="name"
 cx="50%"
 cy="50%"
 outerRadius={90}
 label
 >
 {poData.map((entry, index) => (
 <Cell
 key={`cell-${index}`}
 fill={COLORS[index % COLORS.length]}
 />
 ))}
 </Pie>
 <Tooltip />
 <Legend verticalAlign="bottom" height={36} />
 </PieChart>
 )}
 </Card.Body>
 </Card>
 </Col>

 {/* RECENT POs */}
 <Col md={6}>
 <Card className="shadow-sm border-0">
 <Card.Body>
 <h5 className="mb-3 fw-bold">Recent Purchase Orders</h5>

 {recentPOs.length === 0 ? (
 <p className="text-muted">No recent POs for this project.</p>
 ) : (
 <ul className="list-unstyled mb-0">
 {recentPOs.map((po) => (
 <li key={po._id} className="mb-2">
 <strong>{po.poNumber || po._id}</strong> —{" "}
 {po.vendor?.name || "Unknown Vendor"} —{" "}
 <Badge className="rounded-pill" bg={statusColor(po.status)}>
 {po.status || "Unknown"}
 </Badge>
 </li>
 ))}
 </ul>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ================================
 FULL PO DETAILS TABLE
 ================================= */}
 <Card className="shadow-sm border-0 mt-4">
 <Card.Body>
 <h5 className="mb-3 fw-bold">Detailed Purchase Orders</h5>

 {allPOs.length === 0 ? (
 <p className="text-muted">No purchase orders found.</p>
 ) : (
 <Table responsive bordered hover className="align-middle">
 <thead>
 <tr>
 <th>#</th>
 <th>PO No</th>
 <th>Vendor</th>
 <th>Amount</th>
 <th>Status</th>
 <th>Date</th>
 <th>PDF</th>
 </tr>
 </thead>
 <tbody>
 {allPOs.map((po, index) => (
 <tr key={po._id}>
 <td>{index + 1}</td>
 <td>{po.poNumber || "—"}</td>
 <td>{po.vendor?.name || "—"}</td>
 <td>₹{po.totalAmount?.toLocaleString() || 0}</td>
 <td>
 <Badge className="rounded-pill" bg={statusColor(po.status)}>
 {po.status || "Unknown"}
 </Badge>
 </td>
 <td>{formatDate(po.createdAt)}</td>
 <td>
 <VerifiedCopyActions 
 id={po._id} 
 type="purchase-orders" 
 hasVerifiedCopy={!!po.verifiedCopy?.filePath} 
 onUploadSuccess={fetchPOs}
 />
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </Card.Body>
 </Card>

 {/* PDF Preview Modal */}
 <Modal show={showPdf} onHide={() => setShowPdf(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title>PO PDF Preview</Modal.Title>
 </Modal.Header>
 <Modal.Body style={{ height: "80vh" }}>
 {pdfURL ? (
 <iframe
 src={pdfURL}
 title="PO PDF"
 width="100%"
 height="100%"
 style={{ border: "none" }}
 />
 ) : (
 <p className="text-center">No PDF Loaded</p>
 )}
 </Modal.Body>
 </Modal>

 {loadingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{ background: "rgba(0,0,0,0.4)", zIndex: 9999 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Loading PDF…</h6>
 </div>
 </div>
 )}
 </>
 );
};

export default POTab;
